import { Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams, useSearchParams } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { FlowProvider } from "@/contexts/FlowContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { StaffOrgProvider } from "@/contexts/StaffOrgContext";
import { AppShell } from "@/components/AppShell";
import ScrollToTop from "@/components/ScrollToTop";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { OperatorHomeRedirect } from "@/components/OperatorHomeRedirect";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import RecruitingDashboard from "./pages/RecruitingDashboard";
import Applicant from "./pages/Applicant";
import Tenants from "./pages/Tenants";
import InquiryTimeline from "./pages/InquiryTimeline";
import ApplicantFlow from "./pages/ApplicantFlow";
import VoucherFlow from "./pages/VoucherFlow";
import VerifiedResult from "./pages/VerifiedResult";
import ManualReviewResult from "./pages/ManualReviewResult";
import OAuthCallback from "./pages/OAuthCallback";
import ResetPassword from "./pages/ResetPassword";
import CookiePolicy from "./pages/CookiePolicy";
import BiometricPolicy from "./pages/BiometricPolicy";
import DemoHome from "./pages/DemoHome";
import DemoApplicant from "./pages/DemoApplicant";
import DemoNewApplicantFlow from "./pages/DemoNewApplicantFlow";
import NotFound from "./pages/NotFound";
import { REVIEW_PAGES } from "./demoReviewPages";
import { DEMO_SURFACES_IN_BUILD, IS_PRODUCTION } from "@/lib/env";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { RESET_PASSWORD_PATH, useRecoveryLanding } from "@/hooks/useRecoveryLanding";
import {
  ARTICLE_APPLICANT_PATH,
  ARTICLE_APPLICATION_PATH,
  ARTICLE_JOB_PATH,
  ARTICLE_REVIEW_FLOW,
  ARTICLE_VOUCHER_PATH,
} from "@/lib/articleIntegration";
import {
  FEATHER_APPLICANT_PATH,
  FEATHER_APPLICANT_CHECKPOINT_PATH,
  FEATHER_ATS_PATH,
  FEATHER_DEMO_SETUP_PATH,
  FEATHER_LIVE_CHECKPOINT_PATTERN,
  FEATHER_REVIEW_FLOW,
  FEATHER_VOUCHER_PATH,
} from "@/lib/featherIntegration";

const queryClient = new QueryClient();

/**
 * The partner review pages come from `REVIEW_PAGES` (demoReviewPages.ts), read
 * only behind `DEMO_SURFACES_IN_BUILD`, so a production build drops them. The
 * routes below stay registered in every build; only their element changes
 * (see the React #300 note further down).
 */
const review = REVIEW_PAGES;


/**
 * Back-compat redirect for legacy /case-review and /applicant-b/:inquiryId
 * URLs. Forwards to the unified /applicant/:id page when an id is present,
 * otherwise sends the operator back to the dashboard.
 */
const CaseReviewRedirect = () => {
  const params = useParams<{ inquiryId?: string }>();
  const [search] = useSearchParams();
  const id = params.inquiryId ?? search.get("inquiry_id") ?? search.get("id");
  return <ProtectedRoute><Navigate to={id ? `/applicant/${id}` : "/dashboard"} replace /></ProtectedRoute>;
};

export const AppRoutes = () => (
  <Routes>
    <Route path="/login" element={<Login />} />
    <Route path="/reset-password" element={<ResetPassword />} />
    <Route path="/cookie-policy" element={<CookiePolicy />} />
    {/* Public biometric retention policy — linked from the Plaid IDV
        consent dialog. BIPA §15(a) requires a publicly available
        written policy. See PLAID_IDV_COMPLIANCE_AUDIT.md §4.1. */}
    <Route path="/biometric-policy" element={<BiometricPolicy />} />

    {/* Article review routes. The job/application entry recreates the ATS
        handoff; the two flow routes mount the same components used by live
        magic links. Live Article traffic uses /verify/:token and
        /voucher-flow on the Article tenant hostname. These are demo surfaces:
        not-found in production and behind the operator login everywhere else,
        like /demo. Do not make them public. */}
    <Route
      path="/article"
      element={!DEMO_SURFACES_IN_BUILD || IS_PRODUCTION ? <NotFound /> : <ProtectedRoute><Navigate to={ARTICLE_JOB_PATH} replace /></ProtectedRoute>}
    />
    <Route
      path={ARTICLE_JOB_PATH}
      element={!DEMO_SURFACES_IN_BUILD || IS_PRODUCTION ? <NotFound /> : <ProtectedRoute><Suspense fallback={null}><review.articleJobPosting /></Suspense></ProtectedRoute>}
    />
    <Route
      path={ARTICLE_APPLICATION_PATH}
      element={!DEMO_SURFACES_IN_BUILD || IS_PRODUCTION ? <NotFound /> : <ProtectedRoute><Suspense fallback={null}><review.articleApplication /></Suspense></ProtectedRoute>}
    />
    <Route
      path={ARTICLE_APPLICANT_PATH}
      element={
        !DEMO_SURFACES_IN_BUILD || IS_PRODUCTION ? (
          <NotFound />
        ) : (
          <ProtectedRoute><ApplicantFlow presentationFlow={ARTICLE_REVIEW_FLOW} /></ProtectedRoute>
        )
      }
    />
    <Route
      path={ARTICLE_VOUCHER_PATH}
      element={
        !DEMO_SURFACES_IN_BUILD || IS_PRODUCTION ? (
          <NotFound />
        ) : (
          <ProtectedRoute>
            <VoucherFlow presentationFlow={ARTICLE_REVIEW_FLOW} />
          </ProtectedRoute>
        )
      }
    />

    {/* The second partner's review walkthrough. It mounts the same production
        flow components and recruiting fixture as Article; only the partner
        identity and visual tokens change. Same gating as the Article routes
        above. */}
    <Route path="/feather" element={!DEMO_SURFACES_IN_BUILD || IS_PRODUCTION ? <NotFound /> : <ProtectedRoute><Suspense fallback={null}><review.featherShowcase /></Suspense></ProtectedRoute>} />
    <Route
      path={FEATHER_APPLICANT_PATH}
      element={!DEMO_SURFACES_IN_BUILD || IS_PRODUCTION ? <NotFound /> : <ProtectedRoute><Suspense fallback={null}><review.featherApplicantThankYou /></Suspense></ProtectedRoute>}
    />
    <Route
      path={FEATHER_APPLICANT_CHECKPOINT_PATH}
      element={
        !DEMO_SURFACES_IN_BUILD || IS_PRODUCTION ? (
          <NotFound />
        ) : (
          <ProtectedRoute><ApplicantFlow presentationFlow={FEATHER_REVIEW_FLOW} /></ProtectedRoute>
        )
      }
    />
    {/* A live demo run: the real applicant journey for a session the presenter
        started, the same component /verify/:token renders, under the preview
        bar. The token makes it live; the fixture never applies. */}
    <Route
      path={FEATHER_LIVE_CHECKPOINT_PATTERN}
      element={!DEMO_SURFACES_IN_BUILD || IS_PRODUCTION ? <NotFound /> : <ProtectedRoute><ApplicantFlow /></ProtectedRoute>}
    />
    <Route
      path={FEATHER_DEMO_SETUP_PATH}
      element={!DEMO_SURFACES_IN_BUILD || IS_PRODUCTION ? <NotFound /> : <ProtectedRoute><Suspense fallback={null}><review.featherDemoSetup /></Suspense></ProtectedRoute>}
    />
    <Route
      path={FEATHER_VOUCHER_PATH}
      element={
        !DEMO_SURFACES_IN_BUILD || IS_PRODUCTION ? (
          <NotFound />
        ) : (
          <ProtectedRoute>
            <VoucherFlow presentationFlow={FEATHER_REVIEW_FLOW} />
          </ProtectedRoute>
        )
      }
    />
    <Route
      path={FEATHER_ATS_PATH}
      element={!DEMO_SURFACES_IN_BUILD || IS_PRODUCTION ? <NotFound /> : <ProtectedRoute><Suspense fallback={null}><review.featherAtsIntegration /></Suspense></ProtectedRoute>}
    />

    {/* Public applicant / voucher flows (magic-link based). Outside production,
        an OAuth sign-in that started in this tab's live Feather demo run is
        sent back under the preview bar; with no run in the tab, which is every
        real applicant and voucher, the page renders unchanged
        (FeatherDemoReturn). */}
    <Route
      path="/verify/:token"
      element={
        DEMO_SURFACES_IN_BUILD && !IS_PRODUCTION ? (
          <review.featherDemoReturn kind="applicant"><ApplicantFlow /></review.featherDemoReturn>
        ) : (
          <ApplicantFlow />
        )
      }
    />
    <Route
      path="/voucher-flow"
      element={
        DEMO_SURFACES_IN_BUILD && !IS_PRODUCTION ? (
          <review.featherDemoReturn kind="voucher"><VoucherFlow /></review.featherDemoReturn>
        ) : (
          <VoucherFlow />
        )
      }
    />
    <Route path="/oauth-error" element={<OAuthCallback />} />
    <Route path="/oauth-success" element={<OAuthCallback />} />
    <Route path="/verified" element={<VerifiedResult />} />
    <Route path="/manual-review" element={<ManualReviewResult />} />

    {/* Authenticated operator routes */}
    {/* `/` sends the operator to whichever dashboard their org runs. The
        decision needs the membership, so it lives in a component that reads
        the same react-query cache ProtectedRoute already fills — not a
        hardcoded path. See src/lib/operatorHome.ts. */}
    <Route path="/" element={<ProtectedRoute><OperatorHomeRedirect /></ProtectedRoute>} />
    <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
    {/*
      Recruiting namespace. Registered UNCONDITIONALLY, like the demo block
      below — the element is what varies, never the <Routes> children list.
      See the React #300 note there before adding a sibling here.

      Flat routes rather than a layout route with an <Outlet />: the only
      thing a layout would contribute today is the theme, and AppShell
      already applies that by path (src/lib/theme.ts). A layout route earns
      its place when these pages need shared chrome of their own.
    */}
    <Route path="/recruiting" element={<ProtectedRoute><Navigate to="/recruiting/dashboard" replace /></ProtectedRoute>} />
    <Route path="/recruiting/dashboard" element={<ProtectedRoute><RecruitingDashboard /></ProtectedRoute>} />
    <Route path="/applicant/:id" element={<ProtectedRoute><Applicant /></ProtectedRoute>} />
    {/* Admin pages — self-gated via Google sign-in (not ProtectedRoute). Served on admin.digavouch.com. */}
    <Route path="/tenants" element={<Tenants />} />
    <Route path="/inquiries" element={<InquiryTimeline />} />
    <Route path="/inquiries/:inquiryId" element={<InquiryTimeline />} />

    {/*
      Demo routes — always registered so the <Routes> children list is
      identical across builds. On production each demo path renders
      <NotFound /> instead of the real demo component. The earlier
      pattern (a conditional Fragment between Route children) wrapped
      these in `{!IS_PRODUCTION && (<>...</>)}` and produced a `false`
      child between Routes on prod — that triggered React Router's
      "element type is invalid" / "objects are not valid as a React
      child" path in some renders (prod incident 2026-06-07; React
      error #300 caught by ErrorBoundary). DO NOT revert to the
      conditional-fragment pattern.
      Pair with the BrandFooter gate in src/components/BrandHeader.tsx
      that hides the demo links from operators on prod.
    */}
    {/* Demo index — one footer link instead of three. Auth-gated because
        every route it links to is; a public hub would only bounce the
        visitor to /login on the first click. */}
    <Route
      path="/demo"
      element={IS_PRODUCTION ? <NotFound /> : <ProtectedRoute><DemoHome /></ProtectedRoute>}
    />
    <Route
      path="/demo/applicant-flow"
      element={IS_PRODUCTION ? <NotFound /> : <ProtectedRoute><ApplicantFlow /></ProtectedRoute>}
    />
    <Route
      path="/demo-new/applicant-flow"
      element={IS_PRODUCTION ? <NotFound /> : <ProtectedRoute><DemoNewApplicantFlow /></ProtectedRoute>}
    />
    <Route
      path="/applicant-flow"
      element={IS_PRODUCTION ? <NotFound /> : <Navigate to="/demo/applicant-flow" replace />}
    />
    <Route
      path="/demo/voucher-flow"
      element={IS_PRODUCTION ? <NotFound /> : <ProtectedRoute><VoucherFlow /></ProtectedRoute>}
    />
    {/* Demo operator view — static showcase, no backend wiring. */}
    <Route
      path="/demo/applicant/:slug"
      element={IS_PRODUCTION ? <NotFound /> : <ProtectedRoute><DemoApplicant /></ProtectedRoute>}
    />
    {/* Back-compat redirects — the unified applicant review page lives at /applicant/:id. */}
    <Route path="/applicant-b/:inquiryId" element={<CaseReviewRedirect />} />
    <Route path="/applicant-b" element={<Navigate to="/dashboard" replace />} />
    <Route path="/case-review" element={<CaseReviewRedirect />} />

    <Route path="*" element={<NotFound />} />
  </Routes>
);

/**
 * Claims a password-recovery landing before any auth guard can see it.
 *
 * A recovery link arrives ALREADY AUTHENTICATED, so `/` and `/login` both read
 * it as an ordinary signed-in operator and forward to /dashboard — and the
 * operator never gets the chance to set a password. See
 * `src/lib/recoveryLanding.ts` for the full account.
 *
 * The check runs during render and wraps the whole route table, so
 * <ProtectedRoute> never mounts and there is no competing `replace`
 * navigation to race.
 */
const RecoveryGate = () => {
  const { pathname } = useLocation();
  const recovering = useRecoveryLanding(pathname);
  if (recovering && pathname !== RESET_PASSWORD_PATH) {
    return <Navigate to={RESET_PASSWORD_PATH} replace />;
  }
  return <AppRoutes />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider delayDuration={100} skipDelayDuration={50}>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <StaffOrgProvider>
            <FlowProvider>
              <ErrorBoundary>
                <AppShell>
                  <RecoveryGate />
                </AppShell>
              </ErrorBoundary>
            </FlowProvider>
          </StaffOrgProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
